import React, { Dispatch, SetStateAction } from "react";
import { DateTime, WeekdayNumbers } from "luxon";
import i18n from "i18next";
import { AppDispatch } from "../../../app/store";
import { setInputs } from "../../../features/Booking-Form/slices/booking.slice";
import {
  BookingType,
  NotSerializedBookingInputs,
  NotSerializedBookingMode
} from "../../../features/Booking-Form/typings/booking-inputs";
import { UserOrEmailMapping } from "../../../features/Admin/typings/admin.types";
import { BR, RestrictionZone } from "../../../features/Booking-Form/typings/booking.types";
import { TeamMember } from "../../../features/Booking-Form/typings/team-member";
import { HomeOfficeRatio } from "../../../features/Dashboard/dashboard.component";
import {
  AdvancedBookingSettings,
  MetaData,
  PricingModel,
  UserInformation
} from "../../../features/Login/typings/login.types";
import { MS } from "../../Dialog/form-dialog.component";
import { Weekdays } from "../../Pickers/WeekdayPicker/weekday-picker.component";
import { Colleague } from "../../../features/Connections/types/Colleague.type";
import { AppSettings } from "../../../features/Settings/typings/user-settings.types";
import { initBookingTime } from "../functions/date-time.functions";
import * as _ from "lodash";
import { WeekdayNames } from "../../FacilityManager/Domain/Types/FloorPlan/WeekdayNames.type";
import { getDateValueType } from "../DateTimePicker/functions/getDateValueType.function";

// refactor this to make it testable!
/* istanbul ignore next */
export const WorkingDays = (mobileWorkingWeekdays: Weekdays | undefined) =>
  !mobileWorkingWeekdays?.sunday ||
  !mobileWorkingWeekdays?.saturday ||
  !mobileWorkingWeekdays?.friday ||
  !mobileWorkingWeekdays?.thursday ||
  !mobileWorkingWeekdays?.wednesday ||
  !mobileWorkingWeekdays?.tuesday ||
  !mobileWorkingWeekdays?.monday;

export const hoursLeftPolicy = (homeOfficeRatio: HomeOfficeRatio) => {
  if (
    homeOfficeRatio.minMobileWorkingBookingHours - homeOfficeRatio.mobileWorkingBookingHours <
    0
  ) {
    return 0;
  } else {
    return homeOfficeRatio.minMobileWorkingBookingHours - homeOfficeRatio.mobileWorkingBookingHours;
  }
};

export const homeOfficePolicyText = (homeOfficeRatio: HomeOfficeRatio) => {
  if (homeOfficeRatio.minMobileWorkingBookingHours === 0) {
    return i18n.t("no policy");
  } else {
    return `${hoursLeftPolicy(homeOfficeRatio)}  ${i18n.t("hours left")}`;
  }
};

export const automatedBookingTextToggle = (automatedBookingActivated: boolean) =>
  automatedBookingActivated
    ? i18n.t("_automatedWorkplaceBookingToggle")
    : i18n.t("_automatedWorkplaceBookingToggleOff");

/** @deprecated not consider GROWTH model anymore */
export const enterpriseOrGrowth = (pricingModels: PricingModel[]) =>
  pricingModels.includes(PricingModel.ENTERPRISE) || pricingModels.includes(PricingModel.GROWTH);

export const initSetTeam = (
  inputs: NotSerializedBookingInputs,
  setTeamBookingMember: React.Dispatch<React.SetStateAction<TeamMember[]>>,
  userInformation: UserInformation
) => {
  if (inputs?.usersBookedFor?.length !== 0 && inputs?.usersBookedFor) {
    setTeamBookingMember(inputs?.usersBookedFor);
  } else {
    setTeamBookingMember([
      {
        firstName: userInformation.firstName,
        surname: userInformation.surname,
        isExternal: false,
        userId: userInformation.sub,
        photoUrl: userInformation.photoUrl,
        email: userInformation.email
      }
    ]);
  }
};

export const getBookingType = (mode: NotSerializedBookingMode) => {
  if (mode === BookingType.TEAM) {
    return BookingType.WORKPLACE;
  }
  return mode;
};

export const handleActivityBasedRestriction = (
  inputs: NotSerializedBookingInputs,
  dispatch: AppDispatch,
  setUsers: Dispatch<SetStateAction<UserOrEmailMapping[]>>,
  users: UserOrEmailMapping[],
  sub: string,
  remoteUsers: Colleague[]
) => {
  if (!inputs.activityBasedBooking) {
    dispatch(setInputs({ zoneAccess: [] }));
    if (users.length === 0) {
      const data = remoteUsers.filter((user: { userId: string }) => user.userId !== sub);
      setUsers(data as any);
    }
  }
};

/**
 * @description Takes the input restriction object and sets it to the booking form inputs, then continues to next step.
 * @version 0.1.0
 */
export const continueStepRestriction = (
  teamRestrictions: RestrictionZone,
  dispatch: AppDispatch,
  nextStep: () => void
) => {
  if (teamRestrictions.company) {
    dispatch(
      setInputs({
        restrictionObject: {
          company: teamRestrictions.company,
          object: teamRestrictions.object
        }
      })
    );
    nextStep();
  } else alert(i18n.t("You haven't selected any company!"));
};

export const handleDialogRestriction = (
  modal: MS<BR[]>,
  setModal: Dispatch<SetStateAction<MS<BR[]>>>,
  dispatch: AppDispatch,
  inputs: NotSerializedBookingInputs,
  teamRestrictions: RestrictionZone,
  nextStep: () => void,
  pricingModels: PricingModel[]
) => {
  const del = modal.data;
  if (!del) return;
  const serialized = del.map(r => ({
    ...r,
    start: r.start,
    end: r.end
  }));
  dispatch(setInputs({ deletedRestrictionObjects: serialized }));
  if (inputs.restrictionObject.object.type || !pricingModels.includes(PricingModel.ENTERPRISE)) {
    continueStepRestriction(teamRestrictions, dispatch, nextStep);
  } else setModal({ isOpen: false });
};

export function findMaxAdvancedBookingSettingPerType(
  mode: NotSerializedBookingMode,
  advancedBookingSettings: AdvancedBookingSettings[] | undefined
): number {
  if (!advancedBookingSettings?.length) return 6;

  const map: Partial<Record<BookingType, BookingType>> = {
    [BookingType.WORKPLACE]: BookingType.WORKPLACE,
    [BookingType.TEAM]: BookingType.WORKPLACE,
    [BookingType.PARKINGPLACE]: BookingType.PARKINGPLACE,
    [BookingType.ELECTRICCHARGINGSTATIONPLACE]: BookingType.ELECTRICCHARGINGSTATIONPLACE,
    [BookingType.CONFERENCEZONE]: BookingType.CONFERENCEZONE,
    [BookingType.VEHICLE]: BookingType.VEHICLE,
    [BookingType.VISITOR]: BookingType.VISITOR
  };

  // mode could be null → fallback to 6
  if (!mode) return 6;

  const lookupType = map[mode] ?? undefined;
  if (!lookupType) return 6;

  return advancedBookingSettings.find(s => s.bookingType === lookupType)?.maxWeeksInAdvance ?? 6;
}

export const filterExistingExternal = (
  inputs: NotSerializedBookingInputs,
  userInformation: UserInformation,
  options: TeamMember[]
) => {
  if (inputs.usersBookedFor && inputs.usersBookedFor.length > 0) {
    // deduct current user from the array
    const deductCurrentUser = inputs.usersBookedFor.filter(
      user =>
        user.userId !== userInformation.sub ||
        user.firstName !== userInformation.firstName ||
        user.surname !== userInformation.surname
    );
    // deduct option(team member) from the array
    const existingExternal = deductCurrentUser.filter(
      user => !options.some(option => option.userId === user.userId)
    );

    if (existingExternal.length) {
      const uniqExternal = _.uniqWith(
        existingExternal,
        (extA, extB) =>
          // first and surname can be same so check email too
          extA.firstName === extB.firstName &&
          extA.surname === extB.surname &&
          extA.email === extB.email
      );

      return uniqExternal;
    }
  }
  return [];
};

/**
 * compare the booking inputs date/time with now/day and initialize the booking time
 * When selected end date is max date and max has a time set: use standard end time if it fits within max time, else use max time.
 * @version 0.1.2
 */
export const updateInitialBookingTimeByNow = (
  inputs: NotSerializedBookingInputs,
  settings: AppSettings,
  maxDate: string,
  dispatch: AppDispatch
) => {
  if (!inputs.bookingFrom) return;

  // When end date is max date and max has a time set: choose standard end time if within max, else max time.
  // If today is the max date and max time is already past (e.g. max 16:00, now 17:00), use standard end time.
  if (getDateValueType(maxDate, inputs.bookingTo) && settings.standardEndTime) {
    const maxTime = DateTime.fromISO(maxDate).toFormat("HH:mm");
    const nowTime = DateTime.local().toFormat("HH:mm");
    const isMaxDateToday = inputs.bookingTo === DateTime.local().toFormat("yyyy-MM-dd");
    const maxTimeInPast = isMaxDateToday && nowTime > maxTime;

    let endTime: string;
    if (maxTimeInPast) {
      endTime = settings.standardEndTime;
    } else if (settings.standardEndTime <= maxTime) {
      endTime = settings.standardEndTime;
    } else {
      endTime = maxTime;
    }

    return dispatch(
      setInputs({
        bookingStart: settings.standardStartTime,
        bookingEnd: endTime
      })
    );
  }

  // if start date is tomorrow or later, switch back to standard setting time
  if (DateTime.fromISO(inputs.bookingFrom) > DateTime.local()) {
    // when conference or visitor booking, set the initial duration to 60 mins
    if (inputs.mode === BookingType.CONFERENCEZONE || inputs.mode === BookingType.VISITOR) {
      return dispatch(
        setInputs({
          bookingStart: settings.standardStartTime,
          bookingEnd: DateTime.fromISO(settings.standardStartTime)
            .plus({ hour: 1 })
            .toFormat("HH:mm")
        })
      );
    } else {
      return dispatch(
        setInputs({
          bookingStart: settings.standardStartTime,
          bookingEnd: settings.standardEndTime
        })
      );
    }
  }
  // if the case start date is swtiching back same as today, switch back to cal booking time
  if (
    DateTime.fromISO(inputs.bookingFrom).valueOf() ===
    DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).valueOf()
  ) {
    const [initBookingStart, initBookingEnd] = initBookingTime(
      settings.standardStartTime,
      settings.standardEndTime,
      inputs.mode
    );

    dispatch(
      setInputs({
        bookingStart: initBookingStart,
        bookingEnd: initBookingEnd
      })
    );
  }
};

export function calculateMaxEndDate({
  metaData,
  bookingMode
}: {
  metaData: MetaData;
  bookingMode: NotSerializedBookingMode;
}): string {
  // Convert TEAM mode to WORKPLACE internally
  const convertBookingMode = bookingMode === BookingType.TEAM ? BookingType.WORKPLACE : bookingMode;
  // Find the relevant advanced booking setting for this booking mode
  const bookingSettings = metaData.advancedBookingSettings?.find(
    setting => setting.bookingType === convertBookingMode
  );

  // Fallback
  // need to refactor, because, if bookingSettings doesn't exist, then findMaxAdvancedBookingSettingPerType will return 6 by default always
  // if (!bookingSettings) return DateTime.now().plus({ weeks: 6 }).toFormat("yyyy-MM-dd");
  if (!bookingSettings) {
    const weeksToAdd =
      bookingMode === BookingType.PLACEZONE
        ? metaData.maxAdvancedZoneBookingLength
        : findMaxAdvancedBookingSettingPerType(bookingMode, metaData.advancedBookingSettings);

    return DateTime.now().plus({ weeks: weeksToAdd }).toFormat("yyyy-MM-dd");
  }

  // Otherwise, calculate the actual booking end date using getBookingEndDate
  return getBookingEndDate(bookingSettings, metaData.defaultTimezone);
}

export const weekdayToNumber: Record<WeekdayNames, number> = {
  [WeekdayNames.MONDAY]: 1,
  [WeekdayNames.TUESDAY]: 2,
  [WeekdayNames.WEDNESDAY]: 3,
  [WeekdayNames.THURSDAY]: 4,
  [WeekdayNames.FRIDAY]: 5,
  [WeekdayNames.SATURDAY]: 6,
  [WeekdayNames.SUNDAY]: 7
};

/**
 * Calculates the latest allowed booking date based on the given settings and timezone.
 * - Uses `weekday` and optional `time` for calibration (weekday takes priority).
 * - Extends the booking window by `allowedBookingDaysInAdvance` if set, otherwise by `maxWeeksInAdvance`.
 * - Returns the end of the final day as an ISO string. When `time` is set (and not 00:00),
 *   that time is used as the end of the final day; otherwise 23:59:59.999.
 * - Handles invalid numeric values and timezone fallbacks.
 *
 * Calibration (windowStart)
 * Determines the starting point of the booking horizon.
 * If both weekday and time are missing: use current time.
 * If weekday is defined:
 * Aligns to that weekday in the current week.
 * Uses the configured time, or defaults to 00:00.
 * If that day/time is in the future, rolls back by one week to get the most recent valid calibration.
 * If only time is defined:
 * Aligns to today at the given time (or 00:00).
 * If the time is in the future, roll back by one day.
 *
 * Booking horizon
 * allowedBookingDaysInAdvance has priority over maxWeeksInAdvance.
 * Extends the windowStart by the allowed number of days if set.
 * Otherwise, extends by the configured number of weeks.
 * Always returns the end of the final day (23:59:59.999).
 *
 * Edge cases
 * If allowedBookingDaysInAdvance is invalid (NaN), falls back to maxWeeksInAdvance.
 * Always returns an ISO string.
 *
 * Usage Notes for Developers:
 * Weekday calibration takes precedence over week-based limits.
 * Time is optional but included in calibration if provided.
 * Days override weeks; if allowedBookingDaysInAdvance is set, maxWeeksInAdvance is ignored.
 * Ensure that bookingSettings is validated before passing, especially numeric fields.
 *
 * @param bookingSettings.weekday (optional) — calibrates the start of the booking window to a specific day of the week
 * @param bookingSettings.time (optional) — calibrates the start time on the day or weekday
 * @param bookingSettings.allowedBookingDaysInAdvance (optional) — number of days to extend the booking window from the calibrated start
 * @param bookingSettings.maxWeeksInAdvance — normal base parameter and also fallback if days are not defined
 * @returns ISO string of the calculated latest booking end date, aligned to the end of the day.
 */
export function getBookingEndDate(
  bookingSettings: AdvancedBookingSettings,
  timezone: string
): string {
  const zonedNow = DateTime.now().setZone(timezone);
  const now = zonedNow.isValid ? zonedNow : DateTime.now();

  const hasWeekday = !!bookingSettings.weekday;
  const hasTime = !!bookingSettings.time;

  // Horizon definition: Days always override Weeks
  const allowedDays =
    bookingSettings.allowedBookingDaysInAdvance !== null &&
    bookingSettings.allowedBookingDaysInAdvance !== undefined
      ? Number(bookingSettings.allowedBookingDaysInAdvance)
      : null;

  // Guard against invalid numeric values
  if (allowedDays !== null && Number.isNaN(allowedDays)) {
    return now.plus({ weeks: bookingSettings.maxWeeksInAdvance }).toISO();
  }

  const endOfDay = (dt: DateTime) => dt.set({ hour: 23, minute: 59, second: 59, millisecond: 999 });

  const parseTimeOrDefaultMidnight = (time?: string | null) => {
    // Default time is 00:00 when weekday-based calibration is used
    if (!time) {
      return { hour: 0, minute: 0 };
    }
    const [hourStr, minuteStr] = time.split(":");
    return { hour: Number(hourStr), minute: Number(minuteStr) };
  };

  /** When advanced booking sets time, use it as end of the final day; otherwise end of day (23:59). */
  const endOfFinalDay = (dt: DateTime, time?: string | null) => {
    if (time) {
      const { hour, minute } = parseTimeOrDefaultMidnight(time);
      if (hour !== 0 || minute !== 0) {
        return dt.set({ hour, minute, second: 0, millisecond: 0 });
      }
    }
    return endOfDay(dt);
  };

  // ----------------------------------------------------
  // 1) Determine windowStart (calibration)
  //    Calibration is driven ONLY by weekday and/or time
  // ----------------------------------------------------
  let windowStart: DateTime;

  if (!hasWeekday && !hasTime) {
    // No calibration at all
    windowStart = now;
  } else if (hasWeekday) {
    // Weekly calibration (time defaults to 00:00 if missing)
    const { hour, minute } = parseTimeOrDefaultMidnight(bookingSettings.time ?? null);
    const isoWeekday = weekdayToNumber[bookingSettings.weekday!];

    const thisWeekCalibration = now.set({
      weekday: isoWeekday as WeekdayNumbers,
      hour,
      minute,
      second: 0,
      millisecond: 0
    });

    // Use the latest calibration point that is <= now
    windowStart =
      now < thisWeekCalibration ? thisWeekCalibration.minus({ weeks: 1 }) : thisWeekCalibration;
  } else {
    // Daily calibration (time is set, weekday is missing)
    const { hour, minute } = parseTimeOrDefaultMidnight(bookingSettings.time ?? null);

    const todayCalibration = now.set({
      hour,
      minute,
      second: 0,
      millisecond: 0
    });

    // Use today's calibration if already passed, otherwise yesterday's
    windowStart = now < todayCalibration ? todayCalibration.minus({ days: 1 }) : todayCalibration;
  }

  // ----------------------------------------------------
  // 2) Determine booking horizon from windowStart
  //    Days override Weeks. End of final day uses settings.time when set, else 23:59.
  // ----------------------------------------------------
  if (allowedDays !== null) {
    const res = endOfFinalDay(windowStart.plus({ days: allowedDays }), bookingSettings.time);
    return res.toISO() ?? now.plus({ days: allowedDays }).toISO();
  }

  const res = endOfFinalDay(
    windowStart.plus({ weeks: bookingSettings.maxWeeksInAdvance }),
    bookingSettings.time
  );
  return res.toISO() ?? now.plus({ weeks: bookingSettings.maxWeeksInAdvance }).toISO();
}
